import { createFileRoute, useNavigate, Link } from "@tanstack/react-router"
import { Sun, Moon, Monitor } from "lucide-react"
import { Capacitor } from "@capacitor/core"
import { toast } from "sonner"
import { Bell, MapPin, ChevronRight, FaceId, Fingerprint, Lock } from "@/components/icons"
import { TopBarBackButton } from "@/components/topbar/TopBarBackButton"
import { AppLayout } from "@/components/AppLayout"
import { DesktopPage } from "@/components/desktop/DesktopPage"
import { DesktopPageHeader } from "@/components/desktop/DesktopPageHeader"
import { useIsDesktop } from "@/components/ui/use-mobile"
import { Card } from "@/components/ui/card"
import { MenuItem } from "@/components/ui/menu-item"
import { Switch } from "@/components/ui/switch"
import { useTheme } from "@/components/theme-provider"
import {
  isFaceIdCancel,
  isFingerprintBiometry,
  useBiometryName,
  useBiometryType,
  useFaceIdEnabled,
} from "@/lib/face-id"
import { usePasscodeEnabled } from "@/lib/passcode"

const themeOptions = [
  { value: "light" as const, label: "Light", icon: Sun },
  { value: "dark" as const, label: "Dark", icon: Moon },
  { value: "system" as const, label: "System", icon: Monitor },
]

export const Route = createFileRoute("/_auth/profile/settings/")({
  component: RouteComponent,
})

function FaceIdRow() {
  const { enabled, setEnabled, isLoading } = useFaceIdEnabled()
  const biometryName = useBiometryName()
  const biometryType = useBiometryType()

  const onToggle = async (next: boolean) => {
    try {
      await setEnabled(next)
    } catch (error) {
      if (!isFaceIdCancel(error)) {
        toast.error(error instanceof Error ? error.message : `Couldn't update ${biometryName}`)
      }
    }
  }

  return (
    <div className="flex min-h-[36px] items-center gap-3 px-3 py-2">
      <span className="shrink-0 text-content-primary-bold [&>svg]:size-5">
        {isFingerprintBiometry(biometryType) ? <Fingerprint /> : <FaceId />}
      </span>
      <div className="flex flex-1 flex-col items-start justify-center min-w-0 text-left">
        <span className="text-sm font-medium leading-normal tracking-[0.07px] text-content-primary-bold">
          {biometryName}
        </span>
        <span className="text-xs leading-normal tracking-[0.18px] text-content-primary-subtle">
          Unlock Pronteer
        </span>
      </div>
      <Switch
        checked={enabled}
        disabled={isLoading}
        onCheckedChange={(next) => void onToggle(next)}
      />
    </div>
  )
}

function PasscodeRow() {
  const navigate = useNavigate()
  const { enabled } = usePasscodeEnabled()

  return (
    <MenuItem
      leftIcon={<Lock />}
      rightIcon={<ChevronRight />}
      secondaryText={enabled ? "On" : "Off"}
      size="large"
      onClick={() => navigate({ to: "/profile/settings/passcode" })}
    >
      Passcode
    </MenuItem>
  )
}

function RouteComponent() {
  const navigate = useNavigate()
  const isDesktop = useIsDesktop()
  const { theme } = useTheme()

  const currentTheme = themeOptions.find((t) => t.value === theme) ?? themeOptions[2]

  const body = (
    <Card className="flex flex-col gap-2 w-full px-2">
        <MenuItem
          leftIcon={<currentTheme.icon />}
          rightIcon={<ChevronRight />}
          secondaryText={currentTheme.label}
          size="large"
          onClick={() => navigate({ to: "/profile/settings/appearance" })}
        >
          Appearance
        </MenuItem>
        <hr className="border-border-L1" />
        <MenuItem
          leftIcon={<Bell />}
          rightIcon={<ChevronRight />}
          secondaryText="Push alerts"
          size="large"
          onClick={() => navigate({ to: "/profile/settings/notifications" })}
        >
          Notifications
        </MenuItem>
        <hr className="border-border-L1" />
        <MenuItem
          leftIcon={<MapPin />}
          rightIcon={<ChevronRight />}
          secondaryText="Time, temperature"
          size="large"
          onClick={() => navigate({ to: "/profile/settings/regional" })}
        >
          Regional
        </MenuItem>
        {Capacitor.isNativePlatform() && (
          <>
            <hr className="border-border-L1" />
            <FaceIdRow />
            <hr className="border-border-L1" />
            <PasscodeRow />
          </>
        )}
      </Card>
  )

  if (isDesktop) {
    return (
      <DesktopPage>
        <DesktopPageHeader
          title="Account Settings"
          breadcrumb={
            <span>
              <Link to="/profile" className="hover:underline">
                Profile
              </Link>
              <span className="text-content-primary-subtle"> / Account Settings</span>
            </span>
          }
        />
        {body}
      </DesktopPage>
    )
  }

  return (
    <AppLayout topBar={<TopBarBackButton title="Account Settings" />}>
      {body}
    </AppLayout>
  )
}
